import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { VacationAvatar } from '../../VacationDpTable/components/VacationAvatar';
import { HistoryMilestonePath } from '../../HistoryMilestonePath';
import { CollapsibleRegion } from '@/components/shared/CollapsibleRegion';
import { HistorySegmentsBlock } from './HistorySegmentsBlock';
import type { HistoryRequestRow, HistoryEventKind } from '../../../models/vacationHistoryReport.types';
import { HISTORY_CARD_LABELS, HISTORY_CARD_CLASSES } from '../vacationHistoryResultsCards.definitions';

interface HistoryResultCardProps {
    row: HistoryRequestRow;
    kind: HistoryEventKind;
    periodFilterKey: string | null;
    highlightedKind?: string;
    onSelectRequest?: (row: HistoryRequestRow) => void;
    defaultTimelineOpen?: boolean;
    timelineTourTarget?: string;
}

export function HistoryResultCard({
    row,
    kind,
    periodFilterKey,
    highlightedKind,
    onSelectRequest,
    defaultTimelineOpen = false,
    timelineTourTarget,
}: HistoryResultCardProps) {
    const [isTimelineOpen, setIsTimelineOpen] = useState(defaultTimelineOpen);
    const isClickable = !!row.requestId;
    const hasTimeline = !row.isManual && row.milestones.length > 0;
    const timelineId = `timeline-${row.requestId || `manual-${row.collaborator.id}-${row.segments[0]?.startDate}`}`;

    return (
        <div
            className={clsx(HISTORY_CARD_CLASSES.container, isClickable && HISTORY_CARD_CLASSES.containerClickable)}
            onClick={() => isClickable && onSelectRequest?.(row)}
        >
            <div className="space-y-4">
                <div className={HISTORY_CARD_CLASSES.row}>
                    {/* Header: Avatar, Name, Email, Status */}
                    <div className={HISTORY_CARD_CLASSES.identity}>
                        <VacationAvatar name={row.collaborator.name} size="lg" />
                        <div className="min-w-0 flex-1">
                            <h3 className={HISTORY_CARD_CLASSES.name}>{row.collaborator.name}</h3>
                            <p className={HISTORY_CARD_CLASSES.email}>{row.collaborator.email}</p>
                        </div>
                    </div>
                    <span
                        className={clsx(
                            HISTORY_CARD_CLASSES.status,
                            HISTORY_CARD_CLASSES.statusColumn,
                            row.currentStatusClass || 'bg-gray-100 text-gray-700'
                        )}
                    >
                        {row.currentStatus}
                    </span>

                    {/* Férias Block */}
                    <div className={HISTORY_CARD_CLASSES.segmentsColumn}>
                        <HistorySegmentsBlock row={row} kind={kind} periodFilterKey={periodFilterKey} />
                    </div>
                </div>

                {/* Timeline Button or Manual Entry Info */}
                {hasTimeline ? (
                    <>
                        <button
                            type="button"
                            className={HISTORY_CARD_CLASSES.timelineButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsTimelineOpen(!isTimelineOpen);
                            }}
                            aria-expanded={isTimelineOpen}
                            aria-controls={timelineId}
                        >
                            {HISTORY_CARD_LABELS.timeline}
                            <ChevronDownIcon
                                className={clsx(HISTORY_CARD_CLASSES.chevronIcon, isTimelineOpen && 'rotate-180')}
                                aria-hidden="true"
                            />
                        </button>

                        <CollapsibleRegion id={timelineId} isOpen={isTimelineOpen}>
                            <div className={HISTORY_CARD_CLASSES.divider} data-tour={timelineTourTarget}>
                                <p className={HISTORY_CARD_CLASSES.label}>{HISTORY_CARD_LABELS.timeline}</p>
                                <HistoryMilestonePath milestones={row.milestones} highlightedKind={highlightedKind as any} />
                            </div>
                        </CollapsibleRegion>
                    </>
                ) : (
                    <div className={HISTORY_CARD_CLASSES.divider}>
                        <p className="text-xs text-gray-500">{HISTORY_CARD_LABELS.manualEntry}</p>
                    </div>
                )}
            </div>
        </div>
    );
}
